import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pbEngineer } from '../lib/pb';
import { useToast } from '../shared/components/ToastProvider';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';
import { JOB_TYPES, templateFor } from './jobTemplates';

// New job sheet (spec §2.2): pick an assigned engineer_jobs record + a
// job-type template → create a draft engineer_job_sheets row. The template
// becomes the checklist in form_data, rendered by JobSheetDetail.
export default function JobSheetNew() {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [jobType, setJobType] = useState('boiler_service');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const engineerId = pbEngineer.authStore.record?.id;
    if (!engineerId) return;

    pbEngineer.collection('engineer_jobs').getFullList({
      filter: `engineer_id="${engineerId}" && status != "completed" && status != "cancelled"`,
      sort: '-created',
      expand: 'client_id',
    })
      .then((records) => {
        setJobs(records);
        if (records[0]) setSelectedJobId(records[0].id);
      })
      .catch((err) => console.error('Failed to load jobs:', err))
      .finally(() => setLoading(false));
  }, []);

  const selectedJob = jobs.find((j) => j.id === selectedJobId);

  const handleCreate = async () => {
    if (!selectedJobId) {
      notify('Select a job first', 'error');
      return;
    }
    setCreating(true);
    try {
      const engineer = pbEngineer.authStore.record;
      const template = templateFor(jobType);
      const formData = {
        job_type: jobType,
        checklist: template.checklist.map((item) => ({ label: item, done: false })),
        notes_hint: template.notesHint || '',
        notes: '',
        materials: [],
        photos: [],
      };
      const rec = await pbEngineer.collection('engineer_job_sheets').create({
        tenant_id: engineer?.tenant_id,
        engineer_job_id: selectedJobId,
        client_id: selectedJob.client_id,
        form_data: JSON.stringify(formData),
        status: 'draft',
        work_summary: '',
      });
      notify('Job sheet created — fill it in and complete it on site', 'success');
      navigate(`../jobs/${rec.id}`, { relative: 'path' });
    } catch (err) {
      console.error('Failed to create job sheet:', err);
      notify(err.message || 'Failed to create job sheet', 'error');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="page-container"><Skeleton rows={6} /></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>New Job Sheet</h1>
        <button className="secondary-button" onClick={() => navigate('..', { relative: 'path' })}>
          <SafeIcon icon={Icons.ArrowLeft} size={15} />
          Back to Job Sheets
        </button>
      </div>

      {jobs.length === 0 ? (
        <EmptyState
          title="No active jobs"
          message="You need an assigned, not-yet-completed job to create a sheet for it."
          icon={Icons.Wrench}
        />
      ) : (
        <div className="sheet-form">
          <div className="detail-panel">
            <h3>1 · Choose the job</h3>
            <div className="form-field">
              <label htmlFor="job-picker">Assigned job</label>
              <select
                id="job-picker"
                value={selectedJobId}
                onChange={(e) => setSelectedJobId(e.target.value)}
              >
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.title} — {job.expand?.client_id?.name || job.address || ''}
                  </option>
                ))}
              </select>
              {selectedJob?.address && <p className="form-hint">{selectedJob.address}</p>}
            </div>
          </div>

          <div className="detail-panel">
            <h3>2 · Job type template</h3>
            <p className="form-hint">Picks the checklist used on the sheet (stored in form_data).</p>
            <div className="job-type-grid" style={{ marginTop: 10 }}>
              {JOB_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  className={`job-type-card ${jobType === type.value ? 'selected' : ''}`}
                  onClick={() => setJobType(type.value)}
                >
                  <SafeIcon icon={Icons[type.icon] || Icons.Clipboard} size={18} />
                  <strong>{type.label}</strong>
                  <span>{templateFor(type.value).checklist.length} checklist items</span>
                </button>
              ))}
            </div>
          </div>

          <div className="detail-panel">
            <h3>3 · Optional notes</h3>
            <div className="form-field">
              <label htmlFor="sheet-notes">Notes</label>
              <textarea
                id="sheet-notes"
                placeholder="Anything the office should know before you start…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="form-actions">
            <button className="primary-button" onClick={handleCreate} disabled={creating}>
              <SafeIcon icon={Icons.Plus} size={16} />
              {creating ? 'Creating…' : 'Create Draft Job Sheet'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
